/**
 * Images et documents lus dans un dépôt (ARTO-131, spec 2026-10-11 § 8.5) :
 * les lots qu'on crée depuis un dépôt, la revue du rapprochement des images
 * avec les œuvres, et les décisions de la galerie sur les fichiers douteux.
 * `docs/dev/reprise.md` § Images et documents depuis un dépôt et
 * `docs/dev/api.md` § 12.17 donnent le contrat.
 *
 * Aucune vignette : la revue ne porte que des noms, des chemins et des
 * identifiants, jamais une URL de fichier du dépôt. En mode `?demo`, aucune
 * requête réseau (`demoImages.ts`).
 */
import { modeDemo, requete, ApiError } from './api.js';
import { demoImages } from './demoImages.js';
import { demoReprise } from './demoReprise.js';
import type { CompteursRapprochement, LotReprise } from './reprise.js';

// ── Revue du rapprochement (`rapprochement.json`) ────────────────────────

export type MotifAmbiguite =
  'plusieurs_oeuvres' | 'plusieurs_ordres' | 'meme_ordre' | 'decision_invalide';
export type MotifOrphelin = 'aucune_oeuvre' | 'sans_dossier' | 'artiste_inconnu';
export type MotifIgnore = 'decision' | 'doublon';

/** Une œuvre que le nom du fichier peut désigner, telle que l'API la rend. */
export interface CandidatImage {
  oeuvreId: string;
  inventaire: string;
  titre: string;
  /** Le rang que le nom donne ; `null` : le nom n'en dit rien. */
  ordre: number | null;
}

export interface ImageAmbigue {
  fichierId: string;
  chemin: string;
  motif: MotifAmbiguite;
  candidats: CandidatImage[];
}

export interface ImageOrpheline {
  fichierId: string;
  chemin: string;
  motif: MotifOrphelin;
  cles: string[];
}

export interface ImageRapprochee {
  fichierId: string;
  chemin: string;
  oeuvreId: string;
  inventaire: string;
  titre: string;
  ordre: number;
  octets: number;
  /** Rattachée par une décision de la galerie. */
  decide: boolean;
}

export interface ImageIgnoree {
  fichierId: string;
  chemin: string;
  motif: MotifIgnore;
}

export interface ImageNonPrise {
  fichierId: string;
  chemin: string;
  mime: string;
  /** Ce qu'il faut en faire (un BMP : le réenregistrer). */
  raison?: string;
}

export interface OeuvreSansImage {
  oeuvreId: string;
  inventaire: string;
  titre: string;
}

export interface MorceauRapprochement {
  rang: number;
  fichiers: number;
  octets: number;
}

/**
 * La revue d'un rapprochement. Les ambigus sont tous listés ; les autres
 * listes comptent 5 000 entrées au plus, alors que `compteurs` reste exact.
 */
export interface Rapprochement {
  version: number;
  lotId: string;
  etabliLe: string;
  compteurs: CompteursRapprochement & { decisionsSansFichier?: number };
  ambigus: ImageAmbigue[];
  orphelins: ImageOrpheline[];
  rapproches: ImageRapprochee[];
  ignores: ImageIgnoree[];
  nonPrisEnCharge: ImageNonPrise[];
  oeuvresSansImage: OeuvreSansImage[];
  morceaux: MorceauRapprochement[];
}

// ── Décisions ────────────────────────────────────────────────────────────

export type DecisionImage =
  { action: 'rattacher'; oeuvreId: string; ordre?: number } | { action: 'ignorer' };

/** Une décision par fichier du dépôt (`fichierId`). */
export type DecisionsImages = Record<string, DecisionImage>;

/** Deux jeux de décisions disent-ils la même chose ? */
export function memesDecisions(a: DecisionsImages, b: DecisionsImages): boolean {
  const cles = Object.keys(a);
  if (cles.length !== Object.keys(b).length) return false;
  return cles.every((cle) => {
    const x = a[cle];
    const y = b[cle];
    if (!x || !y || x.action !== y.action) return false;
    if (x.action === 'ignorer') return true;
    return y.action === 'rattacher' && x.oeuvreId === y.oeuvreId && x.ordre === y.ordre;
  });
}

/** Combien de fichiers ont une décision ajoutée, changée ou retirée entre deux jeux ? */
export function nombreChangements(courantes: DecisionsImages, rangees: DecisionsImages): number {
  const ids = new Set([...Object.keys(courantes), ...Object.keys(rangees)]);
  let nombre = 0;
  for (const id of ids) {
    const x = courantes[id];
    const y = rangees[id];
    if (!x || !y || !memesDecisions({ id: x }, { id: y })) nombre += 1;
  }
  return nombre;
}

// ── Appels ───────────────────────────────────────────────────────────────

const base = (idGalerie: string): string =>
  `/v1/console/galleries/${encodeURIComponent(idGalerie)}/reprise`;
const cheminLot = (idGalerie: string, lotId: string): string =>
  `${base(idGalerie)}/lots/${encodeURIComponent(lotId)}`;

export interface CreationLotImages {
  depotId: string;
  /** Sous-dossier du dépôt ; vide : tout le dépôt. */
  dossier: string;
  remplacerPrincipale: boolean;
}

export async function creerLotImages(
  idGalerie: string,
  corps: CreationLotImages,
): Promise<LotReprise> {
  if (modeDemo()) return demoImages.creerLotImages(idGalerie, corps);
  const dossier = corps.dossier.trim();
  return requete<LotReprise>(`${base(idGalerie)}/lots`, {
    methode: 'POST',
    corps: {
      depotId: corps.depotId,
      nature: 'images',
      dossier: dossier === '' ? null : dossier,
      remplacerPrincipale: corps.remplacerPrincipale,
    },
  });
}

export interface CreationLotFichiers {
  depotId: string;
  nature: 'documents' | 'factures-historiques';
  /** Le manifeste tabulaire du dépôt, par son identifiant. */
  manifesteId: string;
  /** Documents seulement. */
  confidentiels: boolean;
}

export async function creerLotFichiers(
  idGalerie: string,
  corps: CreationLotFichiers,
): Promise<LotReprise> {
  if (modeDemo()) return demoReprise.depuisDepot(idGalerie, corps);
  return requete<LotReprise>(`${base(idGalerie)}/lots`, {
    methode: 'POST',
    corps: {
      depotId: corps.depotId,
      nature: corps.nature,
      fichiers: [corps.manifesteId],
      ...(corps.nature === 'documents' ? { confidentiels: corps.confidentiels } : {}),
    },
  });
}

/** Lance (ou relance) le rapprochement : un travail de fond, suivi par le lot. */
export async function rapprocherLot(idGalerie: string, lotId: string): Promise<LotReprise> {
  if (modeDemo()) return demoImages.rapprocher(idGalerie, lotId);
  return requete<LotReprise>(`${cheminLot(idGalerie, lotId)}/rapprocher`, { methode: 'POST' });
}

export async function lireRapprochement(idGalerie: string, lotId: string): Promise<Rapprochement> {
  if (modeDemo()) return demoImages.rapprochement(idGalerie, lotId);
  return requete<Rapprochement>(`${cheminLot(idGalerie, lotId)}/rapprochement`);
}

/** Les décisions déjà rangées du lot ; aucune : `{}`. */
export async function lireDecisions(idGalerie: string, lotId: string): Promise<DecisionsImages> {
  if (modeDemo()) return demoImages.decisions(idGalerie, lotId);
  try {
    const brut = await requete<{ decisions?: DecisionsImages }>(
      `${cheminLot(idGalerie, lotId)}/surcharges`,
    );
    return brut.decisions ?? {};
  } catch (erreur) {
    if (erreur instanceof ApiError && erreur.status === 404) return {};
    throw erreur;
  }
}

/**
 * Range les décisions de la galerie (elles remplacent les précédentes). Le
 * rapprochement est défait : le lot revient à « déposé » et se rapproche de
 * nouveau. `409 lot_modifie` si le lot a changé depuis qu'on l'a lu.
 */
export async function enregistrerDecisions(
  idGalerie: string,
  lotId: string,
  corps: { revision: number; decisions: DecisionsImages },
): Promise<LotReprise> {
  if (modeDemo()) return demoImages.enregistrerDecisions(idGalerie, lotId, corps);
  return requete<LotReprise>(cheminLot(idGalerie, lotId), {
    methode: 'PATCH',
    corps: { revision: corps.revision, surcharges: { decisions: corps.decisions } },
  });
}

// ── Lecture d'un lot ─────────────────────────────────────────────────────

/** Un lot d'images tiré d'un dépôt : la fiche des décisions et des morceaux. */
export function estLotImages(lot: Pick<LotReprise, 'parcours' | 'nature' | 'origine'>): boolean {
  return (
    lot.parcours === 'images' ||
    (lot.parcours === undefined && lot.nature === 'images' && lot.origine === 'depot')
  );
}

/** Un lot de documents ou de factures dont le manifeste et les pièces sont dans un dépôt. */
export function estLotFichiers(lot: Pick<LotReprise, 'parcours'>): boolean {
  return lot.parcours === 'fichiers';
}

/** Combien de fichiers douteux attendent encore une décision ? Ceux de l'API. */
export function ambigusRestants(lot: Pick<LotReprise, 'rapprochement'>): number {
  return lot.rapprochement?.compteurs?.ambigus ?? 0;
}

/** Le dépôt a été réinventorié depuis la création du lot : il faut en créer un autre. */
export function depotReinventorie(message: string | null | undefined): boolean {
  return message != null && /réinventorié/i.test(message);
}

/** Les types d'un manifeste de documents ou de factures : CSV ou XLSX. */
export const TYPES_MANIFESTE: readonly string[] = [
  'text/csv',
  'text/plain',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

/** Une page d'une liste de la revue, à partir de 1. */
export function pageDe<T>(
  liste: readonly T[],
  page: number,
  parPage: number,
): { lignes: T[]; pages: number; page: number } {
  const pages = Math.max(1, Math.ceil(liste.length / parPage));
  const courante = Math.min(Math.max(1, page), pages);
  return {
    lignes: liste.slice((courante - 1) * parPage, courante * parPage),
    pages,
    page: courante,
  };
}

// ── Étapes d'un lot d'images ─────────────────────────────────────────────

export const ETAPES_IMAGES = ['depose', 'rapproche', 'tranche', 'essai', 'a_lancer'] as const;
export type EtapeImages = (typeof ETAPES_IMAGES)[number];

/** Où en est le lot d'images : la première étape non faite est la courante. */
export function etapesImages(
  lot: Pick<LotReprise, 'rapprochement' | 'morceaux'>,
): { etape: EtapeImages; etat: 'fait' | 'courante' | 'a_venir' }[] {
  const morceaux = lot.morceaux?.liste ?? [];
  const rapproche = lot.rapprochement?.fait === true;
  const faites: Record<EtapeImages, boolean> = {
    depose: true,
    rapproche,
    tranche: rapproche && ambigusRestants(lot) === 0,
    essai: morceaux.length > 0 && morceaux.every((morceau) => morceau.etat !== 'depose'),
    a_lancer:
      morceaux.length > 0 &&
      morceaux.every((morceau) => morceau.etat === 'termine' || morceau.etat === 'scelle'),
  };
  let courante = false;
  return ETAPES_IMAGES.map((etape) => {
    if (faites[etape]) return { etape, etat: 'fait' as const };
    if (!courante) {
      courante = true;
      return { etape, etat: 'courante' as const };
    }
    return { etape, etat: 'a_venir' as const };
  });
}
